import { locationLabel, organizationPath, type PublicOrganizationProfile } from "@/lib/publicOrganizationProfile";
import { canonicalUrl } from "@/lib/seo";

/**
 * Basic listings (#1039, page #1040): a staff-listed, ownerless organization
 * that shows identity facts only. Spec:
 * docs/superpowers/specs/2026-10-22-listed-organizations-design.md, plan
 * decisions F2–F4 in docs/growth/2026-10-21-sacramento-metro-buildout-plan.md.
 *
 * This module is the one home for how the browser recognizes a listing and
 * for the page's two outbound targets, so the signed-out and signed-in pages
 * cannot drift.
 */

/**
 * "Tell your Story" (the owner invitation on a listing) has two targets, chosen by
 * FEATURE_FLAGS.ORG_CLAIM_REQUESTS in ListedOrganizationView:
 * - on: the in-app request flow at orgClaimPath (src/pages/OrgClaimPage.tsx);
 * - off: claimMailto, the support-email stopgap, where staff check the person
 *   works there, then send the bound invitation from the Build page.
 * Remove claimMailto once the flag is on in both environments.
 */

/** The in-app claim request page for a listing. */
export function orgClaimPath(slug: string): string {
  return `/claim-organization/${encodeURIComponent(slug)}`;
}

/**
 * The inbox for "Request removal" and "Report a correction". The app has no
 * shared support-address constant; this is the address the agent sidebar
 * already links to. Founder-confirmed on 2026-10-22.
 */
export const LISTING_SUPPORT_EMAIL = "support@compaire.co";

/**
 * The one banner line a listed page shows (F4), followed by its "Tell your
 * Story" link. It names Compaire as the source and invites the owner, so the
 * page says both once. `noun` comes from orgKindNoun: a team is never called a
 * brokerage.
 */
export function listingBannerText(orgName: string, noun: string): string {
  return `${orgName}'s Story was started by Compaire from public sources. Is this your ${noun}?`;
}

/**
 * The short provenance label for surfaces that summarize a listing without
 * inviting the owner (Explore QuickView).
 */
export const LISTING_PROVENANCE_LABEL = "Basic listing · Prepared by Compaire from public sources";

/** The identity facts a listed page renders, from either reader. */
export interface ListedOrgIdentity {
  name: string;
  slug: string;
  type: string | null;
  /** Staff-confirmed logo (F3); null shows the monogram. */
  logoUrl: string | null;
  city: string | null;
  state: string | null;
  website: string | null;
  phone: string | null;
  streetAddress: string | null;
  postalCode: string | null;
  /** "City, ST" labels. */
  offices: string[];
}

const text = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);

/**
 * True only when the anonymous RPC says so. An environment that has not
 * applied the listing migration sends no `listing_kind`, and every profile it
 * returns is an owned one.
 */
export function isListedPublicProfile(p: PublicOrganizationProfile | null | undefined): boolean {
  return p?.listing_kind === "listed";
}

export function listedIdentityFromPublic(p: PublicOrganizationProfile): ListedOrgIdentity {
  const offices = (p.offices ?? []).length
    ? p.offices.map((o) => locationLabel(o)).filter((l): l is string => !!l)
    : (p.office_locations ?? []).filter((l) => !!text(l));
  return {
    name: p.name,
    slug: p.slug,
    type: p.type,
    logoUrl: text(p.logo_url),
    city: text(p.city),
    state: text(p.state),
    website: text(p.website),
    phone: text(p.phone),
    streetAddress: text(p.street_address),
    postalCode: text(p.postal_code),
    offices,
  };
}

/**
 * The `organizations` row, as only a member or staff reader gets it for a
 * listing (selected with `*`, so `listed_at` arrives with it). A signed-in
 * non-member gets no row at all and falls back to the public reader
 * (isListedPublicProfile). A listing is ownerless by invariant (an owner
 * joining clears `listed_at`), so `listed_at` alone is the signal.
 */
export function isListedOrganizationRow(row: unknown): boolean {
  if (!row || typeof row !== "object") return false;
  const listedAt = (row as { listed_at?: unknown }).listed_at;
  return typeof listedAt === "string" && listedAt !== "";
}

export function listedIdentityFromRow(row: Record<string, unknown>): ListedOrgIdentity {
  const raw = row.office_locations;
  const offices = Array.isArray(raw)
    ? raw.map((l) => String(l)).filter((l) => l.trim() !== "")
    : typeof raw === "string"
      ? raw.split(",").map((l) => l.trim()).filter(Boolean)
      : [];
  return {
    name: String(row.name ?? ""),
    slug: String(row.slug ?? ""),
    type: text(row.type),
    logoUrl: text(row.logo_url),
    city: text(row.city),
    state: text(row.state),
    website: text(row.website),
    phone: text(row.phone),
    streetAddress: text(row.street_address),
    postalCode: text(row.postal_code),
    offices,
  };
}

/** "1 Main St, Roseville, CA 95678" from whatever parts are present, or null. */
export function listedAddressLine(o: ListedOrgIdentity): string | null {
  const region = [o.state, o.postalCode].filter(Boolean).join(" ");
  const parts = [o.streetAddress, o.city, region || null].filter(Boolean);
  // A city and state alone are already in the header; an address needs a street.
  return o.streetAddress ? parts.join(", ") : null;
}

/** `tel:` target: digits and a leading +, nothing else. */
export function telHref(phone: string): string | null {
  const plus = phone.trim().startsWith("+") ? "+" : "";
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 7 ? `tel:${plus}${digits}` : null;
}

function mailto(subject: string, body: string): string {
  return `mailto:${LISTING_SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function removalMailto(o: Pick<ListedOrgIdentity, "name" | "slug">): string {
  const url = canonicalUrl(organizationPath(o.slug));
  return mailto(
    `Remove listing: ${o.name}`,
    `Please remove the basic listing for ${o.name}.\n\nPage: ${url}\n\nYour name and your role there:\n`,
  );
}

export function claimMailto(o: Pick<ListedOrgIdentity, "name" | "slug">): string {
  const url = canonicalUrl(organizationPath(o.slug));
  return mailto(
    `Managing ${o.name}'s Story on Compaire`,
    `I manage ${o.name} and want to make its Story on Compaire mine.\n\nPage: ${url}\n\nYour name, role and work email:\n`,
  );
}

export function correctionMailto(o: Pick<ListedOrgIdentity, "name" | "slug">): string {
  const url = canonicalUrl(organizationPath(o.slug));
  return mailto(
    `Correction: ${o.name}`,
    `Page: ${url}\n\nWhat is wrong, and what should it say:\n`,
  );
}

/* ─────────────── Explore and Compare (#1041, #1042, decision F4) ───────────────
 *
 * A listing shows identity values only (F2), so there is nothing a fit score
 * could honestly be computed from. The rule every browse surface follows:
 *
 * - it never shows a numeric fit score; it reads "Not enough information to
 *   score";
 * - under Best fit it ranks after every scored organization, and after every
 *   organization that simply has not been scored yet;
 * - anything it has not filled reads "Not shared yet", never a blank, a dash
 *   or a default (product-context "Product honesty": unknown is not zero).
 *
 * Signed-in agents get listings in Explore and Compare only as identity cards
 * from `list_listed_organization_cards()` (src/lib/listedOrganizationsRpc.ts).
 * The server holds F4 too: `org_visible_to` does not admit a listing, so
 * `compute_fit_score` never scores one for a non-member. The client rule below
 * is the second line, so no surface depends on that.
 */

/** Fit-score copy for a listed organization (F4). */
export const LISTED_FIT_LABEL = "Not enough information to score";

/** A field a listed organization has not filled. */
export const LISTED_NOT_SHARED_LABEL = "Not shared yet";

type FitResolver = (orgId: string) => number | null | undefined;

/**
 * The fit resolver every listed-aware surface uses: a listed organization's
 * score is never a number. `undefined` (viewer ineligible, scores loading)
 * passes through; anything else becomes `null`.
 */
export function listedAwareFitResolver(
  resolve: FitResolver,
  isListed: (orgId: string) => boolean,
): FitResolver {
  return (orgId) => {
    const s = resolve(orgId);
    if (!isListed(orgId)) return s;
    return s === undefined ? undefined : null;
  };
}

/** A predicate over the listed ids among `orgs`. */
export function listedIdPredicate(orgs: ReadonlyArray<{ id: string; listed?: boolean }>): (orgId: string) => boolean {
  const ids = new Set(orgs.filter((o) => o.listed === true).map((o) => o.id));
  return (orgId) => ids.has(orgId);
}

/**
 * Published rows plus listing cards, one entry per id. A listing wins over a
 * published row with the same id (a legacy published, ownerless organization
 * that staff later listed), so it is shown as a listing: identity values
 * only, no fit score.
 */
export function mergeWithListings<T extends { id: string }>(published: readonly T[], listings: readonly T[]): T[] {
  const listedIds = new Set(listings.map((l) => l.id));
  return [...published.filter((p) => !listedIds.has(p.id)), ...listings];
}
